import {
    EmploymentType,
    EpisodeStatus,
    OrderType,
    VisitDatetimeField,
    VisitOutcome,
    VisitState,
    VisitWorkflowStep,
} from '@axle-health/graphql-schema';

import { NORTH_STAR_CONTAINER_KEYS } from '../containerKeys';
import {
    addDays,
    date,
    dateInDays,
    datetime,
    endOfDayInZone,
    startOfDayInZone,
} from '../graphInputHelpers';
import { BuilderContext, FilterInputByEntity, QueueDefinition, RootEntity } from '../types';
import { DEFAULT_CONTAINER_FILTER_WINDOW_DAYS } from './pinnacleServiceIds';

/**
 * North Star is a hospice organization. Its queues are keyed on myUnity service IDs
 * and its episodes are benefit periods, so a recert closes one period and opens the
 * next rather than extending a single episode.
 */

const ADMIT_VISIT_SERVICE_FILTER = {
    externalIds: {
        value: { inList: ['HSRNADMIT', 'HSRNURGENT'] },
        type: { exact: 'SERVICE_ID' },
    },
};

const IS_ACTIVE_BENEFIT_PERIOD_FILTER = {
    isActiveByDate: { exact: true },
    isActiveField: { exact: true },
};

const ADMITTED_BENEFIT_PERIOD_FILTER = {
    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
    visits: {
        services: ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
    },
};

const ADMITTED_BENEFIT_PERIOD_1_FILTER = {
    ...ADMITTED_BENEFIT_PERIOD_FILTER,
    numberInSequence: { exact: 1 },
};

// Symptom follow-up visit services, across RN, LPN and their PRN variants.
const SFV_SERVICE_FILTER = {
    services: {
        externalIds: {
            type: { exact: 'SERVICE_ID' },
            value: {
                inList: ['HSRNMISSEDSFV', 'HSLPNSFVPRN', 'HSLPNSFV', 'HSRNSFVPRN', 'HSRNSFV'],
            },
        },
    },
};

/**
 * Holds the queues to visits recent enough to still be worth acting on.
 *
 * Several queues share the cutoff, and it exists as much for the query planner as
 * for the reader: without it the scan reaches the whole visit history.
 */
const visitsStartingSince = ({ now }: BuilderContext): FilterInputByEntity[RootEntity.VISIT] => ({
    startDatetime: {
        gte: datetime(addDays(now, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS)),
    },
});

// Admit visits for active patients that carry no date yet, whose fulfillment window
// has not long closed or was never set.
const ADMIT_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.rnAdmitVisits,
    label: 'Admits Needing Scheduling',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        services: ADMIT_VISIT_SERVICE_FILTER,
        state: { exact: VisitState.UNSCHEDULED },
        AND: {
            fulfillmentWindowEndDate: {
                gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            },
            OR: {
                fulfillmentWindowEndDate: { isNull: true },
            },
        },
    }),
};

// Patients admitted in the last three days whose care team does not yet cover every
// discipline their visits need. Completed visits count here, because a completed
// admit with no RN on the team is exactly the case to catch.
const RECENT_ADMITS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.recentAdmits,
    label: 'Recent Admits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: {
            isActive: { exact: true },
            hasEpisodes: {
                status: { exact: EpisodeStatus.ACTIVE },
                hasVisits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
        services: ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
        startDatetime: {
            gte: startOfDayInZone(dateInDays(now, timezone, -3), timezone),
            lte: endOfDayInZone(date(now, timezone), timezone),
        },
    }),
};

// Active recurring orders holding more than one occurrence whose visits still need a
// service picked.
const NEW_FREQUENCIES: QueueDefinition<RootEntity.ORDER> = {
    key: NORTH_STAR_CONTAINER_KEYS.newFrequencies,
    label: 'New Frequencies',
    rootEntity: RootEntity.ORDER,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        patient: { isActive: { exact: true } },
        orderType: { exact: OrderType.FREQUENCY },
        isActive: { exact: true },
        maxActiveOccurrences: { gt: 1 },
        activeVisits: {
            outcome: { exact: VisitOutcome.NONE },
            workflowStep: { inList: [VisitWorkflowStep.NEEDS_SERVICE] },
        },
    }),
};

// Benefit periods holding a visit that still needs a service, clinician or date,
// reaching two weeks back so a just-ended period still surfaces its work.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: NORTH_STAR_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: { gte: dateInDays(now, timezone, -14) },
        hasVisits: { needsAssignment: { exact: true } },
    }),
};

// Unassigned visits whose window closes within three days, or closed recently.
const EXPIRING_FULFILLMENT_WINDOW: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.expiringFulfillmentWindowVisits,
    label: 'Expiring Fulfillment Windows',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        needsAssignment: { exact: true },
        latestEndDatetime: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            lte: dateInDays(now, timezone, 3),
        },
    }),
};

// Active patients holding a pending visit in a discipline their care team does not
// cover.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: NORTH_STAR_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Teams',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        hasEpisodes: {
            status: { exact: EpisodeStatus.ACTIVE },
            hasVisits: {
                disciplineCoveredByCareTeam: { exact: false },
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// Recent visits still waiting on a clinician.
const UNASSIGNED_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.unassignedVisits,
    label: 'Unassigned',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        patient: { isActive: { exact: true } },
        ...visitsStartingSince(ctx),
        needsClinicians: { exact: true },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

// Patients who have died or been discharged and still hold pending visits to cancel.
const DEATH_DISCHARGE_WITH_PENDING_VISITS: QueueDefinition<RootEntity.PATIENT> = {
    key: NORTH_STAR_CONTAINER_KEYS.deathDischargeWithVisits,
    label: 'Deaths/Discharges with Pending Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        isActive: { exact: false },
        AND: {
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// Pending visits assigned to a clinician who is no longer active, either recent or
// carrying no date at all.
const INACTIVE_CLINICIAN_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: { isActive: { exact: false } },
        AND: {
            ...visitsStartingSince(ctx),
            OR: {
                startDatetime: { isNull: true },
            },
        },
    }),
};

// Benefit periods ending within the window that hold no recert visit, either
// admitted first periods or any later period.
const RECERT: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: NORTH_STAR_CONTAINER_KEYS.recert,
    label: 'Recerts',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: {
            range: {
                start: date(now, timezone),
                end: dateInDays(now, timezone, DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            },
        },
        AND: {
            ...ADMITTED_BENEFIT_PERIOD_1_FILTER,
            OR: {
                ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                numberInSequence: { gt: 1 },
            },
        },
        visits: {
            NOT: {
                services: {
                    externalIds: { value: { exact: 'HSRNRECERT' } },
                },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            },
        },
    }),
};

// Patients owed a HOPE Update Visit.
//
// HUV1 is due on days 6 through 15 after the HOPE start date and HUV2 on days 16
// through 30, and each is late rather than missed for a further ten days. So each
// half bounds how long ago the HOPE period can have started, then excludes the
// patients who already hold that visit: pending anywhere in the due window, or
// completed anywhere in the due window extended by the grace period.
const HUV: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: NORTH_STAR_CONTAINER_KEYS.huv,
    label: 'HUVs',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActiveByDate: { exact: true },
        isActiveField: { exact: true },
        patient: {
            isActive: { exact: true },
            hospice: {
                hopeStartDate: { gte: dateInDays(now, timezone, -25) },
                NOT: {
                    hasVisitInHuv1Window: {
                        services: { externalIds: { value: { exact: 'HSRNHUV1' } } },
                        outcome: { exact: VisitOutcome.NONE },
                    },
                    OR: {
                        hasVisitInHopeWindow: {
                            startDay: 6,
                            endDay: 25,
                            visit: {
                                services: { externalIds: { value: { exact: 'HSRNHUV1' } } },
                                outcome: { exact: VisitOutcome.COMPLETED },
                            },
                        },
                    },
                },
                OR: {
                    hopeStartDate: { gte: dateInDays(now, timezone, -40) },
                    NOT: {
                        hasVisitInHuv2Window: {
                            services: { externalIds: { value: { exact: 'HSRNHUV2' } } },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        OR: {
                            hasVisitInHopeWindow: {
                                startDay: 16,
                                endDay: 40,
                                visit: {
                                    services: { externalIds: { value: { exact: 'HSRNHUV2' } } },
                                    outcome: { exact: VisitOutcome.COMPLETED },
                                },
                            },
                        },
                    },
                },
            },
        },
    }),
};

// Symptom Impact Assessments still owed their follow-up visit.
//
// The assessment itself carries the `sfv_required` marker, and the queue drops it
// once the follow-up has actually happened. Each of the three arms rules out one way
// that can be true: a follow-up completed after the assessment, one missed late
// enough that a further attempt is still owed, or one already booked inside the
// two-day window.
const SFV: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.sfv,
    label: 'SFVs',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        metadata: { hasKey: 'sfv_required' },
        startDatetime: {
            gte: startOfDayInZone(dateInDays(now, timezone, -9), timezone),
        },
        AND: {
            relatedVisitCondition: {
                exists: false,
                samePatient: true,
                sameEpisode: true,
                filter: {
                    ...SFV_SERVICE_FILTER,
                    outcome: { exact: VisitOutcome.COMPLETED },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
            AND: {
                relatedVisitCondition: {
                    exists: false,
                    samePatient: true,
                    sameEpisode: true,
                    filter: {
                        ...SFV_SERVICE_FILTER,
                        outcome: { exact: VisitOutcome.MISSED },
                    },
                    timeConstraints: [
                        {
                            field: VisitDatetimeField.EFFECTIVE_DATE,
                            relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                            // Strictly past the day after, so a miss on that day
                            // still leaves the follow-up owed.
                            gtOffsetMinutes: 1440,
                        },
                    ],
                },
                AND: {
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            ...SFV_SERVICE_FILTER,
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                                lteOffsetMinutes: 2880, // two days
                            },
                        ],
                    },
                },
            },
        },
    }),
};

// Recent visits held in review because the patient did not show.
const PATIENT_NO_SHOWS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.patientNoShows,
    label: 'Patient No-Shows',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        patient: { isActive: { exact: true } },
        ...visitsStartingSince(ctx),
        state: { exact: VisitState.IN_REVIEW },
        outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
    }),
};

// Pending visits in the next week whose clinician has no active shift covering them.
//
// The window is the same seven calendar days either way it is expressed. The date
// arm is end-inclusive, so it runs to today plus six; the timestamp arm has to stop
// before midnight opening the eighth day instead.
const VISITS_WITHOUT_ACTIVE_SHIFTS: QueueDefinition<RootEntity.VISIT> = {
    key: NORTH_STAR_CONTAINER_KEYS.visitsWithoutActiveShifts,
    label: 'Visits Without Shifts',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        hasActiveShiftOnDate: { exact: false },
        outcome: { exact: VisitOutcome.NONE },
        AND: {
            effectiveDate: {
                gte: date(now, timezone),
                lte: dateInDays(now, timezone, 6),
            },
            OR: {
                effectiveDate: { isNull: true },
                latestEndDatetime: {
                    gte: startOfDayInZone(date(now, timezone), timezone),
                    lt: startOfDayInZone(dateInDays(now, timezone, 7), timezone),
                },
            },
        },
    }),
};

/**
 * Qualification codes held by clinicians who are non-treating or on call only.
 *
 * They legitimately carry no territory, team or address, so they are excluded rather
 * than reported as incomplete. Matched on the qualification code, which is unique
 * per org and therefore stable across environments in a way an ID is not.
 */
const NON_TREATING_QUALIFICATION_CODES = ['Manager', 'On Call'];

// Active treating clinicians missing a field Decision Support needs to place them.
// The OR spans several many-to-many joins, so the query is asked for distinct rows.
const INCOMPLETE_CLINICIAN_DETAILS: QueueDefinition<RootEntity.CLINICIAN> = {
    key: NORTH_STAR_CONTAINER_KEYS.incompleteClinicianDetails,
    label: 'Incomplete Clinician Details',
    rootEntity: RootEntity.CLINICIAN,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        isActive: { exact: true },
        DISTINCT: true,
        NOT: {
            qualifications: {
                name: { inList: NON_TREATING_QUALIFICATION_CODES },
            },
        },
        AND: {
            address: { id: { isNull: true } },
            OR: {
                clinicianTeams: { id: { isNull: true } },
                OR: {
                    mobileTerritories: { id: { isNull: true } },
                    OR: {
                        employmentType: { exact: EmploymentType.UNKNOWN },
                    },
                },
            },
        },
    }),
};

export const NORTH_STAR_QUEUES: QueueDefinition[] = [
    ADMIT_VISITS,
    RECENT_ADMITS,
    INCOMPLETE_CARE_TEAM,
    NEW_FREQUENCIES,
    PATIENTS_NEEDING_SCHEDULING,
    UNASSIGNED_VISITS,
    HUV,
    SFV,
    EXPIRING_FULFILLMENT_WINDOW,
    DEATH_DISCHARGE_WITH_PENDING_VISITS,
    INACTIVE_CLINICIAN_VISITS,
    PATIENT_NO_SHOWS,
    RECERT,
    VISITS_WITHOUT_ACTIVE_SHIFTS,
];

// UAT carries one queue production has held back: Incomplete Clinician Details,
// which flags every clinician until the integration ingests team and employment type.
export const NORTH_STAR_UAT_QUEUES: QueueDefinition[] = [
    ...NORTH_STAR_QUEUES,
    INCOMPLETE_CLINICIAN_DETAILS,
];
